/**
 * LandingPage Component
 * Premium landing page with modern glassmorphism design
 * - Lucide React icons for professional look
 * - Gradient accents and glow effects
 * - Smooth animations and hover states
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, FeatureCard } from "../components/common";
import { ROUTES } from "../services/routes";
import { useAppSelector } from "../redux";
import { Footer } from "components/layout";
import { getStandardJobTypes, getAdvancedJobTypes } from "../services/jobTypes";
import { getJobIcon } from "../utils/icons";

// Lucide Icons
import {
  Zap,
  Target,
  Shield,
  Users,
  Building2,
  Factory,
  TreePine,
  Plane,
  Satellite,
  Upload,
  BarChart3,
  UserPlus,
  Sparkles,
  Play,
  ArrowRight,
  Check,
  Menu,
  X,
  Brain,
  Cpu,
  Globe,
  Layers,
  ShieldCheck,
  ShoppingBag,
  Hotel,
  Truck,
  Construction,
  Activity,
  Sprout,
} from "lucide-react";

// Industry icons - unique contextual icons for each industry
const industryIcons: Record<string, React.ReactNode> = {
  "Retail & Shopping Malls": <ShoppingBag className="w-8 h-8" />,
  "Hospitality & Hotels": <Hotel className="w-8 h-8" />,
  Manufacturing: <Factory className="w-8 h-8" />,
  Transportation: <Truck className="w-8 h-8" />,
  Infrastructure: <Construction className="w-8 h-8" />,
  "Wildlife & Conservation": <TreePine className="w-8 h-8" />,
  "Aerospace & Defense": <Plane className="w-8 h-8" />,
  "Geospatial & Agriculture": <Sprout className="w-8 h-8" />,
  "Healthcare & Medical": <Activity className="w-8 h-8" />,
};

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const industries = [
    {
      title: "Retail & Shopping Malls",
      description:
        "People counting, crowd analysis, and customer behavior insights",
    },
    {
      title: "Hospitality & Hotels",
      description:
        "Room readiness inspection, lobby monitoring, and guest experience optimization",
    },
    {
      title: "Manufacturing",
      description:
        "Quality control, defect detection, and production line monitoring",
    },
    {
      title: "Transportation",
      description:
        "License plate recognition, parking management, and traffic analysis",
    },
    {
      title: "Infrastructure",
      description:
        "Pothole detection, road condition monitoring, and asset inspection",
    },
    {
      title: "Wildlife & Conservation",
      description:
        "Wildlife detection, species identification, and habitat monitoring",
    },
    {
      title: "Aerospace & Defense",
      description:
        "Product design analysis, engineering drawing evaluation, and component inspection",
    },
    {
      title: "Geospatial & Agriculture",
      description:
        "Satellite imagery analysis, crop monitoring, and land use assessment",
    },
    {
      title: "Healthcare & Medical",
      description:
        "Medical imaging analysis, diagnostic support, and patient monitoring systems",
    },
  ];

  const standardServices = getStandardJobTypes().map((config) => ({
    icon: getJobIcon(config.iconName),
    title: config.label,
    description: config.description,
    category: "Standard",
  }));

  const advancedServices = getAdvancedJobTypes().map((config) => ({
    icon: getJobIcon(config.iconName),
    title: config.label,
    description: config.description,
    category: "Advanced",
    status: config.status,
    isConfigBased: config.isConfigBased,
    externalLink: config.externalLink,
  }));

  return (
    <div>
      <div className="min-h-screen bg-[#0a0b14] text-white font-sans selection:bg-cyan-500/30 overflow-x-hidden">
        {/* Background Effects */}
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]"></div>
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-purple-900/20 blur-[120px]"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-900/20 blur-[120px]"></div>
        </div>

        {/* Header / Navbar */}
        <nav className="relative z-10 max-w-7xl mx-auto px-2 py-8">
          <div className="glass-nav px-8 rounded-5xl border border-white/10 backdrop-blur-md">
            <div className="flex items-center justify-between h-16 lg:h-20">
              {/* Logo */}
              <button className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <img
                  src="/logo.png"
                  alt="UniVision Logo"
                  className="w-10 h-10 rounded-xl"
                />
                <span className="text-xl font-bold tracking-tight">
                  Uni<span className="text-cyan-400">Vision</span>
                </span>
              </button>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-8">
                <a
                  href="#services"
                  className="text-slate-400 hover:text-white transition-colors text-sm font-medium"
                >
                  Services
                </a>
                <a
                  href="#industries"
                  className="text-slate-400 hover:text-white transition-colors text-sm font-medium"
                >
                  Industries
                </a>
                <a
                  href="#pricing"
                  className="text-slate-400 hover:text-white transition-colors text-sm font-medium"
                >
                  Pricing
                </a>
                <a
                  href="#how-it-works"
                  className="text-slate-400 hover:text-white transition-colors text-sm font-medium"
                >
                  How It Works
                </a>
              </div>

              {/* Auth Buttons */}
              <div className="hidden lg:flex items-center gap-3">
                {!isAuthenticated ? (
                  <>
                    <Button
                      variant="ghost"
                      size="md"
                      onClick={() => navigate(ROUTES.LOGIN)}
                    >
                      Sign In
                    </Button>
                    <Button
                      variant="gradient"
                      size="md"
                      onClick={() => navigate(ROUTES.REGISTER)}
                      // icon={<Sparkles className="w-4 h-4" />}
                      className="bg-gradient-to-r from-purple-600 to-cyan-500 px-6 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-cyan-500/20"
                    >
                      Get Started
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="gradient"
                    size="md"
                    onClick={() => navigate(ROUTES.DASHBOARD)}
                    icon={<BarChart3 className="w-4 h-4" />}
                  >
                    Dashboard
                  </Button>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden text-white p-2 hover:bg-slate-800/50 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <div className="lg:hidden py-4 border-t border-slate-800/50 animate-fade-down">
                <div className="flex flex-col gap-2">
                  <a
                    href="#services"
                    className="text-slate-300 hover:text-white hover:bg-slate-800/50 px-4 py-3 rounded-lg transition-all"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Services
                  </a>
                  <a
                    href="#industries"
                    className="text-slate-300 hover:text-white hover:bg-slate-800/50 px-4 py-3 rounded-lg transition-all"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Industries
                  </a>
                  <a
                    href="#pricing"
                    className="text-slate-300 hover:text-white hover:bg-slate-800/50 px-4 py-3 rounded-lg transition-all"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Pricing
                  </a>
                  <a
                    href="#how-it-works"
                    className="text-slate-300 hover:text-white hover:bg-slate-800/50 px-4 py-3 rounded-lg transition-all"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    How It Works
                  </a>
                  <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-slate-700/50">
                    {!isAuthenticated ? (
                      <>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            navigate(ROUTES.LOGIN);
                            setMobileMenuOpen(false);
                          }}
                          fullWidth
                        >
                          Sign In
                        </Button>
                        <Button
                          variant="gradient"
                          onClick={() => {
                            navigate(ROUTES.REGISTER);
                            setMobileMenuOpen(false);
                          }}
                          fullWidth
                          icon={<Sparkles className="w-4 h-4" />}
                        >
                          Get Started
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="gradient"
                        onClick={() => {
                          navigate(ROUTES.DASHBOARD);
                          setMobileMenuOpen(false);
                        }}
                        fullWidth
                        icon={<BarChart3 className="w-4 h-4" />}
                      >
                        Dashboard
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Main Content */}
        <div className="relative">
          {/* Hero Section */}
          <section className="z-10 max-w-7xl mx-auto px-6 pt-8 pb-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <h1
                  className="text-5xl lg:text-6xl font-bold -tracking-wide"
                  style={{ lineHeight: "1.2" }}
                >
                  <span className="text-purple-400">Enterprise</span> Computer{" "}
                  <br />
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                    Vision AI for
                  </span>{" "}
                  <br />
                  <span className="text-blue-300">Modern Business</span>
                </h1>

                <p className="text-lg text-gray-400 max-w-lg leading-relaxed animate-fade-up">
                  Leverage enterprise-grade computer vision to analyze videos
                  and images with precision. Scale your operations with our
                  next-generation computer vision platform. Real-time insights,
                  automated workflows, and secure data processing.
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row items-center gap-4 animate-fade-up">
                  {!isAuthenticated ? (
                    <>
                      <Button
                        variant="gradient"
                        size="lg"
                        onClick={() => navigate(ROUTES.REGISTER)}
                        icon={<Sparkles className="w-5 h-5" />}
                        glow
                      >
                        Start Free Trial
                      </Button>
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() => navigate(ROUTES.LOGIN)}
                        icon={<Play className="w-5 h-5" />}
                      >
                        Watch Demo
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Tablet Mockup - Tilted with subtle shadow */}
              <div
                className="relative flex items-center justify-center"
                style={{ perspective: "1000px" }}
              >
                {/* Circular gradient shadow behind tablet */}
                <div
                  className="absolute rounded-full -z-10"
                  style={{
                    width: "100%",
                    height: "100%",
                    background:
                      "radial-gradient(ellipse at center, rgba(6, 78, 91, 0.4) 0%, rgba(6, 78, 91, 0.15) 40%, transparent 70%)",
                    filter: "blur(20px)",
                  }}
                />
                <div
                  className="tablet-mockup relative rounded-2xl border-[8px] border-slate-700/80 overflow-hidden bg-[#0f111a]"
                  style={{
                    maxHeight: "380px",
                    transform: "rotateY(-8deg) rotateX(2deg)",
                    boxShadow:
                      "0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.05)",
                  }}
                >
                  <img
                    src="/hero-dashboard.png"
                    alt="Dashboard UI"
                    className="w-full h-auto object-contain"
                    style={{ maxHeight: "364px" }}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Trust Indicators - Logo Carousel */}
          {/* <div className="z-10 max-w-7xl mx-auto px-6 pt-16 pb-12">
            <p className="text-3xl lg:text-4xl font-bold text-white mb-8 text-center">
              Trusted by industry leaders worldwide
            </p>
            <div className="relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-navy-900 to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-navy-900 to-transparent z-10" />

              <div className="flex animate-scroll-x">
                {[
                  "Microsoft",
                  "Google Cloud",
                  "AWS",
                  "Tesla",
                  "Meta",
                  "NVIDIA",
                  "OpenAI",
                  "Intel",
                ].map((company, idx) => (
                  <div
                    key={`logo-1-${idx}`}
                    className="flex-shrink-0 mx-8 flex items-center justify-center h-12 px-6 bg-slate-800/30 rounded-lg border border-slate-700/30 hover:border-primary-500/30 transition-all group"
                  >
                    <span className="text-slate-400 font-semibold text-sm group-hover:text-primary-400 transition-colors whitespace-nowrap">
                      {company}
                    </span>
                  </div>
                ))}
                {[
                  "Microsoft",
                  "Google Cloud",
                  "AWS",
                  "Tesla",
                  "Meta",
                  "NVIDIA",
                  "OpenAI",
                  "Intel",
                ].map((company, idx) => (
                  <div
                    key={`logo-2-${idx}`}
                    className="flex-shrink-0 mx-8 flex items-center justify-center h-12 px-6 bg-slate-800/30 rounded-lg border border-slate-700/30 hover:border-primary-500/30 transition-all group"
                  >
                    <span className="text-slate-400 font-semibold text-sm group-hover:text-primary-400 transition-colors whitespace-nowrap">
                      {company}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div> */}

          {/* Stats Section */}
          <section className="z-10 max-w-7xl mx-auto px-6 pt-8 pb-24">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                <Card
                  variant="glass"
                  padding="lg"
                  className="relative flex flex-col items-center justify-center aspect-[5.5/6] rounded-2xl bg-[#111827] border border-opacity-50 transition-all duration-500 hover:scale-105 group stat-card-blue"
                >
                  <div className="mb-8 p-5 rounded-full flex items-center justify-center relative overflow-hidden icon-bg-blue">
                    <Zap size={40} strokeWidth={2} />
                  </div>
                  <h3 className="text-4xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-cyan-400 to-blue-600 mb-2">
                    20+
                  </h3>
                  <p className="text-slate-200 font-medium text-xl">
                    Analysis Services
                  </p>
                </Card>

                <Card
                  variant="glass"
                  padding="lg"
                  className="relative flex flex-col items-center justify-center aspect-[5.5/6] rounded-2xl bg-[#111827] border border-opacity-50 transition-all duration-500 hover:scale-105 group stat-card-green"
                >
                  <div className="mb-8 p-5 rounded-full flex items-center justify-center relative overflow-hidden icon-bg-green">
                    <Target size={40} strokeWidth={2} />
                  </div>
                  <h3 className="text-4xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-green-300 to-emerald-600 mb-2">
                    95%+
                  </h3>
                  <p className="text-slate-200 font-medium text-xl">
                    Accuracy Rate
                  </p>
                </Card>

                <Card
                  variant="glass"
                  padding="lg"
                  className="relative flex flex-col items-center justify-center aspect-[5.5/6] rounded-2xl bg-[#111827] border border-opacity-50 transition-all duration-500 hover:scale-105 group stat-card-purple"
                >
                  <div className="mb-8 p-5 rounded-full flex items-center justify-center relative overflow-hidden icon-bg-purple from-purple-400 to-indigo-600">
                    <ShieldCheck size={40} strokeWidth={2} />
                  </div>
                  <h3 className="text-4xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-purple-400 to-indigo-600 mb-2">
                    100%
                  </h3>
                  <p className="text-slate-200 font-medium text-xl">
                    Secure & Private
                  </p>
                </Card>
              </div>
            </div>
          </section>

          {/* Industries Section */}
          <section
            id="industries"
            className="z-10 max-w-7xl mx-auto px-6 pt-8 pb-24"
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-xs font-semibold text-primary-400 mb-4 uppercase tracking-widest shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                  <Globe className="w-4 h-4" />
                  INDUSTRIES
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Solutions for Every Sector
                </h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  Tailored computer vision solutions designed for diverse
                  industry requirements
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 lg:gap-6">
                {industries.map((industry, idx) => (
                  <Card
                    key={idx}
                    variant="glass"
                    padding="lg"
                    hover
                    className="group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 text-primary-400 group-hover:scale-110 transition-transform">
                        {industryIcons[industry.title] || (
                          <Building2 className="w-6 h-6" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-primary-300 transition-colors">
                          {industry.title}
                        </h3>
                        <p className="text-slate-400 text-sm leading-relaxed">
                          {industry.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section
            id="services"
            className="z-10 max-w-7xl mx-auto px-6 pt-8 pb-24 scroll-mt-20"
          >
            <div className="container mx-auto">
              <div className="text-center mb-16">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-xs font-semibold text-primary-400 mb-4 uppercase tracking-widest shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                  <Layers className="w-4 h-4" />
                  OUR CAPABILITIES
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Comprehensive Vision AI Services
                </h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  End-to-end computer vision solutions powered by
                  state-of-the-art deep learning
                </p>
              </div>

              {/* Standard Services */}
              <div className="mb-16">
                <div className="flex items-center gap-3 mb-8">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-500/20">
                    <Brain className="w-5 h-5 text-primary-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    Core Analysis Services
                  </h3>
                  <span className="ml-2 px-3 py-1 text-xs font-medium rounded-full bg-primary-500/15 text-primary-400 border border-primary-500/30">
                    {standardServices.length} Services
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                  {standardServices.map((service, idx) => (
                    <FeatureCard
                      key={idx}
                      icon={service.icon}
                      title={service.title}
                      description={service.description}
                      badge={service.category}
                      badgeVariant="primary"
                    />
                  ))}
                </div>
              </div>

              {/* Advanced Services */}
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-500/20">
                    <Cpu className="w-5 h-5 text-accent-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    Advanced Analysis Services
                  </h3>
                  <span className="ml-2 px-3 py-1 text-xs font-medium rounded-full bg-accent-500/15 text-accent-400 border border-accent-500/30">
                    {advancedServices.length} Services
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 lg:gap-6">
                  {advancedServices.map((service, idx) => (
                    <FeatureCard
                      key={idx}
                      icon={service.icon}
                      title={service.title}
                      description={service.description}
                      badge={
                        service.status === "coming_soon"
                          ? "Coming Soon"
                          : service.externalLink
                          ? "External"
                          : service.category
                      }
                      badgeVariant={
                        service.status === "coming_soon"
                          ? "warning"
                          : service.externalLink
                          ? "success"
                          : "accent"
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* How It Works Section */}
          <section
            id="how-it-works"
            className="z-10 max-w-7xl mx-auto px-6 pt-8 pb-24"
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-xs font-semibold text-primary-400 mb-4 uppercase tracking-widest shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                  <Zap className="w-4 h-4" />
                  HOW IT WORKS
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Three Simple Steps
                </h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  Get started with AI-powered analysis in minutes
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                {/* Step 1 */}
                <div className="relative">
                  <Card
                    variant="glass"
                    padding="xl"
                    className="text-center h-full"
                  >
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-primary-500/30">
                      1
                    </div>
                    <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 mx-auto mb-6 mt-4">
                      <UserPlus className="w-8 h-8 text-primary-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      Create Account
                    </h3>
                    <p className="text-slate-400 leading-relaxed">
                      Sign up securely with email, Google, or face recognition
                      authentication
                    </p>
                  </Card>
                  {/* Connector Arrow (hidden on mobile) */}
                  <div className="hidden md:block absolute top-1/2 -right-6 lg:-right-8">
                    <ArrowRight className="w-6 h-6 text-slate-600" />
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative">
                  <Card
                    variant="glass"
                    padding="xl"
                    className="text-center h-full"
                  >
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-primary-500/30">
                      2
                    </div>
                    <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 mx-auto mb-6 mt-4">
                      <Upload className="w-8 h-8 text-primary-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      Upload Media
                    </h3>
                    <p className="text-slate-400 leading-relaxed">
                      Select your analysis type and upload videos, images, or 3D
                      models
                    </p>
                  </Card>
                  {/* Connector Arrow (hidden on mobile) */}
                  <div className="hidden md:block absolute top-1/2 -right-6 lg:-right-8">
                    <ArrowRight className="w-6 h-6 text-slate-600" />
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative">
                  <Card
                    variant="glass"
                    padding="xl"
                    className="text-center h-full"
                  >
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-primary-500/30">
                      3
                    </div>
                    <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 mx-auto mb-6 mt-4">
                      <BarChart3 className="w-8 h-8 text-primary-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      Get Insights
                    </h3>
                    <p className="text-slate-400 leading-relaxed">
                      Receive detailed analytics, annotated outputs, and
                      exportable reports
                    </p>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section
            id="pricing"
            className="z-10 max-w-7xl mx-auto px-6 pt-8 pb-24"
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-xs font-semibold text-primary-400 mb-4 uppercase tracking-widest shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                  <Layers className="w-4 h-4" />
                  PRICING
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Flexible Plans for Every Scale
                </h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  Choose the deployment model that fits your business needs
                </p>
              </div>

              <div className="mx-auto">
                <Card
                  variant="glass"
                  padding="none"
                  className="overflow-hidden"
                >
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-gradient-to-r from-primary-600 to-accent-600">
                          <th className="px-6 py-5 text-white font-semibold border-r border-white/10">
                            Feature
                          </th>
                          <th className="px-6 py-5 text-white font-semibold border-r border-white/10">
                            Basic SaaS
                          </th>
                          <th className="px-6 py-5 text-white font-semibold border-r border-white/10">
                            Agentic Usage
                          </th>
                          <th className="px-6 py-5 text-white font-semibold">
                            IP-Based Solution
                          </th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-300">
                        {[
                          [
                            "Deployment",
                            "Cloud",
                            "Cloud + Agents",
                            "On-prem / Edge",
                          ],
                          [
                            "Pricing Model",
                            "Subscription",
                            "Subscription",
                            "License / Enterprise",
                          ],
                          [
                            "Vision Models",
                            "Standard",
                            "Standard + Custom",
                            "Custom & proprietary",
                          ],
                          [
                            "Anomaly Alerts",
                            "Basic alerts",
                            "Advanced automation",
                            "Fully configurable",
                          ],
                          [
                            "Custom Training",
                            <X className="w-5 h-5 text-slate-500" />,
                            <Check className="w-5 h-5 text-emerald-400" />,
                            <Check className="w-5 h-5 text-emerald-400" />,
                          ],
                          [
                            "API Integration",
                            "Limited",
                            "Full",
                            "Enterprise-grade",
                          ],
                          ["Data Control", "Cloud", "Hybrid", "Full on-prem"],
                          [
                            "Best For",
                            "SMB",
                            "Enterprise automation",
                            "High-security orgs",
                          ],
                        ].map((row, idx) => (
                          <tr
                            key={idx}
                            className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors"
                          >
                            <td className="px-6 py-4 font-medium text-slate-200 border-r border-slate-700/50">
                              {row[0]}
                            </td>
                            <td className="px-6 py-4 border-r border-slate-700/50">
                              {row[1]}
                            </td>
                            <td className="px-6 py-4 border-r border-slate-700/50">
                              {row[2]}
                            </td>
                            <td className="px-6 py-4">{row[3]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <div className="text-center mt-8">
                  <p className="text-slate-500 mb-6">
                    * Contact us for custom enterprise solutions and volume
                    pricing
                  </p>
                  <Button
                    variant="gradient"
                    size="lg"
                    onClick={() =>
                      (window.location.href = "mailto:contact@intellivision.ai")
                    }
                    icon={<ArrowRight className="w-5 h-5" />}
                  >
                    Contact Sales
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="z-10 max-w-7xl mx-auto px-6 pt-8 pb-24">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <Card
                variant="gradient"
                padding="xl"
                className="text-center relative overflow-hidden"
              >
                {/* Background Glow */}
                <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary-500/20 rounded-full blur-[100px]" />
                <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-500/20 rounded-full blur-[100px]" />

                <div className="relative z-10">
                  <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                    Ready to Transform Your Visual Data?
                  </h2>
                  <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
                    Join leading enterprises using UniVision to extract
                    actionable insights from their visual content. Start your
                    journey today.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => navigate(ROUTES.REGISTER)}
                      icon={<Sparkles className="w-5 h-5" />}
                      glow
                    >
                      Start Free Trial
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() =>
                        (window.location.href =
                          "mailto:contact@intellivision.ai")
                      }
                      icon={<ArrowRight className="w-5 h-5" />}
                    >
                      Schedule Demo
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};
